import { useEffect, useMemo, useRef, useState } from 'react';

import { useLocation } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';
import { useUserExperienceContext } from 'bundles/unified-common/contexts/UserExperienceContext';
import {
  type MembershipsForRedirect,
  SelectionAction,
  getRedirectOrSetSelectionAction,
  getRouteContext,
  getSavedUserExperience,
  performRedirectOnce,
} from 'bundles/unified-common/utils/userExperienceRedirectUtils';

export type UseUserExperienceRedirectOptions = {
  memberships?: MembershipsForRedirect | null;
  isMembershipsLoading?: boolean;
};

export type UseUserExperienceRedirectReturn = {
  /** True while resolving user experience or performing redirect; hold rendering to prevent flash. */
  isWaitingForRedirect: boolean;
};

const normalizePathname = (p: string) => (p === '' ? '/' : p);

const useUserExperienceRedirect = (options?: UseUserExperienceRedirectOptions): UseUserExperienceRedirectReturn => {
  const { pathname: routerPathname, search: routerSearch } = useLocation();

  // Route context uses router pathname so "/" and app-relative paths are correct for redirect decisions.
  const pathname = normalizePathname(routerPathname);
  // For buildRedirectUrl use browser pathname when available so path-specific overrides (/browse → org browse) see the real URL.
  const pathnameForRedirect =
    typeof window !== 'undefined' && window.location?.pathname !== undefined
      ? normalizePathname(window.location.pathname)
      : pathname;
  const search =
    typeof window !== 'undefined' && window.location?.search !== undefined
      ? window.location.search
      : routerSearch || '';

  const {
    userExperience,
    isLoading: isUserExperienceLoading,
    isUpdating: isUserExperienceUpdating,
    updateUserExperience,
  } = useUserExperienceContext();
  const memberships = options?.memberships;
  const isMembershipsLoading = options?.isMembershipsLoading ?? false;

  const isOrgScopedHome = GrowthDiscoveryEpicClient.preview('orgScopedHome');
  const hasRedirectedRef = useRef(false);
  const hasSetSelectionRef = useRef(false);
  const [isRedirectInProgress, setIsRedirectInProgress] = useState(false);

  const redirectGuard = useMemo(
    () => ({
      get: () => hasRedirectedRef.current,
      set: (value: boolean) => {
        hasRedirectedRef.current = value;
      },
    }),
    []
  );

  const isWaitingForContext = isUserExperienceLoading || isUserExperienceUpdating;
  const isRedirectDataRelevant = !!isOrgScopedHome && isAuthenticatedUser();
  const isWaitingForRedirectData = isRedirectDataRelevant && isMembershipsLoading;
  const isWaitingForRedirect = isWaitingForContext || isWaitingForRedirectData || isRedirectInProgress;

  useEffect(() => {
    if (
      !isOrgScopedHome ||
      !isAuthenticatedUser() ||
      isUserExperienceLoading ||
      isUserExperienceUpdating ||
      isMembershipsLoading
    ) {
      return;
    }

    const routeContext = getRouteContext(pathname);
    const savedExperience = getSavedUserExperience(userExperience);
    const result = getRedirectOrSetSelectionAction(routeContext, savedExperience, memberships, {
      pathname: pathnameForRedirect,
      search,
    });

    if (result?.action === SelectionAction.REDIRECT) {
      hasSetSelectionRef.current = false;
      setIsRedirectInProgress(true);
      performRedirectOnce(result.url, redirectGuard);
    } else {
      setIsRedirectInProgress(false);
      if (result?.action === SelectionAction.SET_SELECTION) {
        if (!hasSetSelectionRef.current) {
          hasSetSelectionRef.current = true;
          updateUserExperience(result.input);
        }
      } else {
        hasSetSelectionRef.current = false;
      }
    }
  }, [
    pathname,
    pathnameForRedirect,
    search,
    isOrgScopedHome,
    isUserExperienceLoading,
    isUserExperienceUpdating,
    isMembershipsLoading,
    userExperience,
    memberships,
    updateUserExperience,
    redirectGuard,
  ]);

  return { isWaitingForRedirect };
};

export default useUserExperienceRedirect;
